/* Price chart (specification section 13).
 *
 * Canvas-backed rendering with a native crosshair, kinetic pan/zoom and
 * multi-pane support.
 *
 * The chart's on-canvas vendor attribution logo is switched OFF (see the
 * layout options below). The charting library's licence still requires an
 * attribution notice and a link somewhere user-visible, and that obligation is
 * met by the Licences dialog in the header — see THIRD-PARTY-NOTICES.md.
 * Removing the logo does not remove the obligation, it relocates it.
 *
 * Nine series types, thirteen indicators, six timeframes, log/linear scaling,
 * price alerts and a table view of the underlying bars.
 *
 * Two honesty rules run through this panel and both are visible on screen:
 *
 *   1. DATA PROVENANCE. Bars from a real feed and bars generated because no
 *      feed could serve the instrument are never presented alike. The synthetic
 *      case is labelled in the panel title and carries a banner.
 *
 *   2. DERIVED PRICES ARE MARKED. Heikin-Ashi, Renko and Line-Break do not plot
 *      prices anything traded at — a Renko brick's close is a threshold price
 *      crossed, not a print. Reading a stop level off one is a real and common
 *      mistake, so each carries a banner saying what it is showing.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AreaSeries, BarSeries, BaselineSeries, CandlestickSeries, ColorType,
  CrosshairMode, HistogramSeries, LineSeries, LineStyle, createChart,
  type IChartApi, type IPriceLine, type ISeriesApi, type UTCTimestamp,
} from 'lightweight-charts'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { useStore } from '@/store'
import { fmtPrice } from '@/lib/format'
import {
  atr, bollinger, donchian, ema, heikinAshi, lineBreak, macd, obv, renko,
  rsi, sma, stochastic, suggestedBrickSize, supertrend, vwap, wma,
  type Series,
} from '@/lib/indicators'
import type { Bar } from '@/types'

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '1d'] as const
type Tf = (typeof TIMEFRAMES)[number]

const CHART_TYPES = [
  { id: 'candles',   label: 'Candles',     derived: false },
  { id: 'hollow',    label: 'Hollow',      derived: false },
  { id: 'heikin',    label: 'Heikin-Ashi', derived: true  },
  { id: 'bars',      label: 'OHLC bars',   derived: false },
  { id: 'line',      label: 'Line',        derived: false },
  { id: 'area',      label: 'Area',        derived: false },
  { id: 'baseline',  label: 'Baseline',    derived: false },
  { id: 'renko',     label: 'Renko',       derived: true  },
  { id: 'linebreak', label: 'Line break',  derived: true  },
] as const
type ChartType = (typeof CHART_TYPES)[number]['id']

// Overlays draw on the price pane; oscillators get their own pane, because
// plotting an RSI on a price axis is the dual-axis mistake in disguise.
const OVERLAYS = ['SMA', 'EMA', 'WMA', 'VWAP', 'BB', 'DONCHIAN', 'SUPERTREND'] as const
const OSCILLATORS = ['RSI', 'MACD', 'STOCH', 'ATR', 'OBV'] as const
type Overlay = (typeof OVERLAYS)[number]
type Oscillator = (typeof OSCILLATORS)[number]

const css = (name: string, fallback: string) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback

// lightweight-charts plots UTCTimestamps as UTC; NSE traders read IST. Shifting
// live timestamps by the exchange offset makes the axis show session times
// (09:15–15:30) rather than the UTC instants behind them.
const IST_OFFSET_S = 19_800

export function Chart() {
  const instrument = useStore((s) => s.selectedInstrument)
  const theme = useStore((s) => s.theme)

  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const mainRef = useRef<ISeriesApi<
    'Candlestick' | 'Bar' | 'Line' | 'Area' | 'Baseline'> | null>(null)
  const volumeRef = useRef<ISeriesApi<'Histogram'> | null>(null)
  const extraRef = useRef<ISeriesApi<'Line' | 'Histogram'>[]>([])
  const alertLinesRef = useRef<IPriceLine[]>([])
  const legendRef = useRef<HTMLDivElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  // Full screen: the Fullscreen API where the browser has it, a fixed-position
  // class where it does not (iOS Safari). Escape leaves either.
  const [fullscreen, setFullscreen] = useState(false)
  const [cssFullscreen, setCssFullscreen] = useState(false)
  useEffect(() => {
    const sync = () => setFullscreen(wrapRef.current !== null && document.fullscreenElement === wrapRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])
  useEffect(() => {
    if (!cssFullscreen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCssFullscreen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cssFullscreen])
  const toggleFullscreen = () => {
    const el = wrapRef.current
    if (!el) return
    if (fullscreen || cssFullscreen) {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
      setCssFullscreen(false)
      return
    }
    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => setCssFullscreen(true))
    } else {
      setCssFullscreen(true)
    }
  }
  const isFull = fullscreen || cssFullscreen

  // Daily is the default because it is the timeframe that always has data.
  // Minute bars only exist while the market is open, so defaulting to 1m meant
  // every session outside trading hours opened on a SYNTHETIC chart — correct,
  // clearly labelled, and still the worst possible first impression.
  const [timeframe, setTimeframe] = useState<Tf>('1d')
  const [chartType, setChartType] = useState<ChartType>('candles')
  const [overlays, setOverlays] = useState<Set<Overlay>>(new Set(['SMA', 'VWAP']))
  const [oscillator, setOscillator] = useState<Oscillator | null>('RSI')
  const [logScale, setLogScale] = useState(false)
  const [showVolume, setShowVolume] = useState(true)
  const [showTable, setShowTable] = useState(false)
  const [alerts, setAlerts] = useState<number[]>([])
  const [alertDraft, setAlertDraft] = useState('')

  const [bars, setBars] = useState<Bar[]>([])
  const [live, setLive] = useState(false)
  const [loading, setLoading] = useState(false)

  // ---- data ---------------------------------------------------------------
  useEffect(() => {
    if (!instrument) { setBars([]); return }
    let cancelled = false
    let timer: number | undefined

    const load = async () => {
      setLoading(true)
      try {
        const rows = await api.candles(String(instrument), timeframe)
        if (!cancelled && rows.length) { setBars(rows); setLive(true) }
      } catch {
        // No live source for this instrument. A deterministic placeholder is
        // drawn so the panel is not empty, and it is labelled SYNTHETIC
        // everywhere it appears — never passed off as market data.
        if (!cancelled) { setBars(syntheticBars(String(instrument))); setLive(false) }
      } finally {
        if (!cancelled) setLoading(false)
      }
      if (!cancelled && timeframe !== '1d') {
        timer = window.setTimeout(() => void load(), 60_000)
      }
    }
    void load()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [instrument, timeframe])

  // ---- series construction -------------------------------------------------
  const plotted = useMemo<Bar[]>(() => {
    if (bars.length === 0) return []
    switch (chartType) {
      case 'heikin':    return heikinAshi(bars)
      case 'renko':     return renko(bars, suggestedBrickSize(bars))
      case 'linebreak': return lineBreak(bars, 3)
      default:          return bars
    }
  }, [bars, chartType])

  const toTime = useMemo(() => (ms: number) =>
    (Math.floor(ms / 1000) + (live ? IST_OFFSET_S : 0)) as UTCTimestamp, [live])

  // ---- chart lifecycle -----------------------------------------------------
  // Recreated when the type, theme or scale changes: lightweight-charts binds
  // the series kind at creation, and colours are read from CSS custom
  // properties that only resolve at construction time.
  useEffect(() => {
    const el = containerRef.current
    if (!el || !instrument) return

    const colText = css('--text-muted', '#737373')
    const colGrid = css('--grid', 'rgba(148,163,184,0.14)')
    const colUp = css('--buy', '#0f7f63')
    const colDown = css('--sell', '#db0000')
    const colAccent = css('--accent', '#2c78f6')

    const chart = createChart(el, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colText,
        fontSize: 10,
        panes: { separatorColor: colGrid, enableResize: true },
        // No third-party branding on the trading surface. The licence's
        // attribution and link requirement is satisfied by the Licences dialog
        // instead, which is the alternative the library's own documentation
        // describes.
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: colGrid, style: LineStyle.Dotted },
        horzLines: { color: colGrid, style: LineStyle.Dotted },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: {
        borderColor: colGrid,
        mode: logScale ? 1 : 0,   // 1 = logarithmic
        scaleMargins: { top: 0.08, bottom: showVolume ? 0.24 : 0.08 },
      },
      timeScale: {
        borderColor: colGrid,
        // Renko and line-break bars are NOT time-ordered ticks; showing a time
        // axis on them invites reading a duration that does not exist.
        timeVisible: timeframe !== '1d' && chartType !== 'renko' && chartType !== 'linebreak',
        secondsVisible: false,
        rightOffset: 6,
      },
      localization: { priceFormatter: (p: number) => p.toFixed(2) },
    })

    let main: ISeriesApi<'Candlestick' | 'Bar' | 'Line' | 'Area' | 'Baseline'>
    switch (chartType) {
      case 'hollow':
        // Hollow candles: an up candle is an outline, a down candle is filled.
        main = chart.addSeries(CandlestickSeries, {
          upColor: 'transparent', downColor: colDown,
          borderUpColor: colUp, borderDownColor: colDown,
          wickUpColor: colUp, wickDownColor: colDown,
          borderVisible: true,
        })
        break
      case 'bars':
        main = chart.addSeries(BarSeries, {
          upColor: colUp, downColor: colDown, thinBars: true,
        })
        break
      case 'line':
        main = chart.addSeries(LineSeries, { color: colAccent, lineWidth: 2 })
        break
      case 'area':
        main = chart.addSeries(AreaSeries, {
          lineColor: colAccent, lineWidth: 2,
          topColor: `${colAccent}44`, bottomColor: `${colAccent}05`,
        })
        break
      case 'baseline':
        main = chart.addSeries(BaselineSeries, {
          baseValue: { type: 'price', price: bars[0]?.close ?? 0 },
          topLineColor: colUp, topFillColor1: `${colUp}44`, topFillColor2: `${colUp}05`,
          bottomLineColor: colDown, bottomFillColor1: `${colDown}05`,
          bottomFillColor2: `${colDown}44`,
          lineWidth: 2,
        })
        break
      default:
        main = chart.addSeries(CandlestickSeries, {
          upColor: colUp, downColor: colDown,
          wickUpColor: colUp, wickDownColor: colDown,
          borderVisible: false,
        })
    }

    const volume = chart.addSeries(HistogramSeries, {
      priceScaleId: 'vol',
      priceFormat: { type: 'volume' },
      lastValueVisible: false,
      priceLineVisible: false,
      visible: showVolume,
    })
    chart.priceScale('vol').applyOptions({
      scaleMargins: { top: 0.80, bottom: 0 },
      visible: false,
    })

    chartRef.current = chart
    mainRef.current = main
    volumeRef.current = volume
    extraRef.current = []
    alertLinesRef.current = []

    // OHLC legend follows the crosshair. It clears between bars rather than
    // holding the last value, so a stale reading is never mistaken for a live one.
    chart.subscribeCrosshairMove((param) => {
      const legend = legendRef.current
      if (!legend) return
      const d = param.seriesData.get(main) as
        { open?: number; high?: number; low?: number; close?: number; value?: number } | undefined
      if (!d) { legend.textContent = ''; return }
      if (d.open === undefined) {
        legend.innerHTML = `<b>${(d.value ?? 0).toFixed(2)}</b>`
        return
      }
      const chg = d.open ? ((d.close! - d.open) / d.open) * 100 : 0
      legend.innerHTML =
        `O <b>${d.open.toFixed(2)}</b>  H <b>${d.high!.toFixed(2)}</b>  ` +
        `L <b>${d.low!.toFixed(2)}</b>  C <b style="color:${d.close! >= d.open ? colUp : colDown}">` +
        `${d.close!.toFixed(2)}</b> <span style="color:${chg >= 0 ? colUp : colDown}">` +
        `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%</span>`
    })

    const fit = () => chart.applyOptions({ width: el.clientWidth, height: el.clientHeight })
    fit()
    let raf = 0
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(fit)
    })
    ro.observe(el)

    return () => {
      ro.disconnect()
      cancelAnimationFrame(raf)
      chart.remove()
      chartRef.current = null
      mainRef.current = null
      volumeRef.current = null
      extraRef.current = []
      alertLinesRef.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrument, theme, chartType, logScale, showVolume, timeframe])

  // ---- data + indicators ---------------------------------------------------
  useEffect(() => {
    const chart = chartRef.current
    const main = mainRef.current
    const volume = volumeRef.current
    if (!chart || !main || !volume || plotted.length === 0) return

    const colUp = css('--buy', '#0f7f63')
    const colDown = css('--sell', '#db0000')

    const times = plotted.map((b) => toTime(b.time))

    if (chartType === 'line' || chartType === 'area' || chartType === 'baseline') {
      main.setData(plotted.map((b, i) => ({ time: times[i]!, value: b.close })))
    } else {
      main.setData(plotted.map((b, i) => ({
        time: times[i]!, open: b.open, high: b.high, low: b.low, close: b.close,
      })))
    }

    volume.setData(plotted.map((b, i) => ({
      time: times[i]!,
      value: b.volume,
      color: b.close >= b.open ? `${colUp}55` : `${colDown}55`,
    })))

    // Rebuild every derived series. Removing and re-adding is cheap at these
    // sizes and avoids a whole class of stale-series bugs.
    for (const s of extraRef.current) chart.removeSeries(s)
    extraRef.current = []

    const line = (
      data: { time: UTCTimestamp; value: number }[],
      color: string, width: 1 | 2 = 1, pane = 0, dashed = false,
    ) => {
      const s = chart.addSeries(LineSeries, {
        color, lineWidth: width,
        lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
        priceLineVisible: false, lastValueVisible: false,
        crosshairMarkerVisible: false,
      }, pane)
      s.setData(data)
      extraRef.current.push(s)
      return s
    }
    const pts = (vals: Series) =>
      vals.flatMap((v, i) => (v == null ? [] : [{ time: times[i]!, value: v }]))

    // Indicators are computed on the PLOTTED series, so an indicator on a
    // Heikin-Ashi chart describes the smoothed candles the user is actually
    // looking at rather than a different set of numbers underneath them.
    const closes = plotted.map((b) => b.close)

    if (overlays.has('SMA'))  line(pts(sma(closes, 20)), 'var(--series-1)', 2)
    if (overlays.has('EMA'))  line(pts(ema(closes, 20)), 'var(--series-2)', 2)
    if (overlays.has('WMA'))  line(pts(wma(closes, 20)), 'var(--series-6)', 2)
    if (overlays.has('VWAP')) line(pts(vwap(plotted)), 'var(--series-5)', 2)
    if (overlays.has('BB')) {
      const { upper, lower } = bollinger(closes, 20, 2)
      line(pts(upper), 'var(--series-1)', 1, 0, true)
      line(pts(lower), 'var(--series-1)', 1, 0, true)
    }
    if (overlays.has('DONCHIAN')) {
      const { upper, lower } = donchian(plotted, 20)
      line(pts(upper), 'var(--series-4)', 1, 0, true)
      line(pts(lower), 'var(--series-4)', 1, 0, true)
    }
    if (overlays.has('SUPERTREND')) {
      const { line: st, direction } = supertrend(plotted, 10, 3)
      // Split into up-trend and down-trend segments so the colour carries the
      // trend rather than one line ambiguously spanning both.
      const up = st.map((v, i) => (direction[i] === 1 ? v : null))
      const dn = st.map((v, i) => (direction[i] === -1 ? v : null))
      line(pts(up), colUp, 2)
      line(pts(dn), colDown, 2)
    }

    // --- oscillator pane ---
    if (oscillator === 'RSI') {
      const r = line(pts(rsi(closes, 14)), 'var(--series-4)', 2, 1)
      r.createPriceLine({ price: 70, color: colDown, lineWidth: 1,
                          lineStyle: LineStyle.Dashed, title: '70' })
      r.createPriceLine({ price: 30, color: colUp, lineWidth: 1,
                          lineStyle: LineStyle.Dashed, title: '30' })
    } else if (oscillator === 'MACD') {
      const m = macd(closes)
      const hist = chart.addSeries(HistogramSeries, {
        priceLineVisible: false, lastValueVisible: false,
      }, 1)
      hist.setData(m.histogram.flatMap((v, i) => (v == null ? [] : [{
        time: times[i]!, value: v, color: v >= 0 ? `${colUp}99` : `${colDown}99`,
      }])))
      extraRef.current.push(hist)
      line(pts(m.line), 'var(--series-1)', 2, 1)
      line(pts(m.signal), 'var(--series-2)', 2, 1)
    } else if (oscillator === 'STOCH') {
      const st = stochastic(plotted, 14, 3)
      const k = line(pts(st.k), 'var(--series-1)', 2, 1)
      line(pts(st.d), 'var(--series-2)', 2, 1)
      k.createPriceLine({ price: 80, color: colDown, lineWidth: 1,
                          lineStyle: LineStyle.Dashed, title: '80' })
      k.createPriceLine({ price: 20, color: colUp, lineWidth: 1,
                          lineStyle: LineStyle.Dashed, title: '20' })
    } else if (oscillator === 'ATR') {
      line(pts(atr(plotted, 14)), 'var(--series-6)', 2, 1)
    } else if (oscillator === 'OBV') {
      line(pts(obv(plotted)), 'var(--series-3)', 2, 1)
    }

    chart.timeScale().fitContent()
  }, [plotted, chartType, overlays, oscillator, toTime])

  // ---- price alerts --------------------------------------------------------
  // Client-side markers only: these draw a line on the chart. They do NOT place
  // an order and they do not survive a reload, and the panel says so rather
  // than letting a trader believe a level is being watched for them.
  useEffect(() => {
    const main = mainRef.current
    if (!main) return
    for (const l of alertLinesRef.current) main.removePriceLine(l)
    alertLinesRef.current = alerts.map((p) => main.createPriceLine({
      price: p,
      color: css('--warn', '#b25b00'),
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: 'alert',
    }))
  }, [alerts, chartType, theme, logScale, showVolume, timeframe])

  const activeType = CHART_TYPES.find((t) => t.id === chartType)!
  const last = bars[bars.length - 1]
  const first = bars[0]
  const dayChange = last && first ? ((last.close - first.open) / first.open) * 100 : 0

  return (
    <div ref={wrapRef} className={`qx-chart-wrap${cssFullscreen ? ' qx-fullscreen' : ''}`}>
    <Panel
      title={instrument
        ? `${instrument} · ${activeType.label} · ${timeframe}${live ? '' : ' · SYNTHETIC'}`
        : 'Chart'}
      noPadding
      actions={
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <Group>
            {TIMEFRAMES.map((tf) => (
              <Chip key={tf} active={timeframe === tf} onClick={() => setTimeframe(tf)}>
                {tf}
              </Chip>
            ))}
          </Group>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value as ChartType)}
            aria-label="Chart type"
            style={selectStyle}
          >
            {CHART_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <Chip active={logScale} onClick={() => setLogScale((v) => !v)} title="Logarithmic price scale">
            log
          </Chip>
          <Chip active={showVolume} onClick={() => setShowVolume((v) => !v)}>vol</Chip>
          <Chip active={showTable} onClick={() => setShowTable((v) => !v)} title="Table view of the plotted bars">
            table
          </Chip>
          <Chip active={isFull} onClick={toggleFullscreen}
                title={isFull ? 'Exit full screen (Esc)' : 'Full screen chart'}>
            {isFull ? '⤡ exit' : '⛶ full'}
          </Chip>
        </div>
      }
      footer={
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center' }}>
          <Group label="Overlays">
            {OVERLAYS.map((o) => (
              <Chip
                key={o}
                active={overlays.has(o)}
                onClick={() => setOverlays((prev) => {
                  const next = new Set(prev)
                  if (next.has(o)) next.delete(o); else next.add(o)
                  return next
                })}
              >
                {o}
              </Chip>
            ))}
          </Group>

          <Group label="Oscillator">
            {OSCILLATORS.map((o) => (
              <Chip
                key={o}
                active={oscillator === o}
                onClick={() => setOscillator((cur) => (cur === o ? null : o))}
              >
                {o}
              </Chip>
            ))}
          </Group>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              const v = Number(alertDraft)
              if (Number.isFinite(v) && v > 0) { setAlerts((a) => [...a, v]); setAlertDraft('') }
            }}
            style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center', marginLeft: 'auto' }}
          >
            <input
              className="num"
              value={alertDraft}
              onChange={(e) => setAlertDraft(e.target.value)}
              placeholder="alert @"
              aria-label="Add a price alert line"
              style={{ ...selectStyle, width: 78 }}
            />
            <button type="submit" style={{ ...selectStyle, cursor: 'pointer' }}>+</button>
            {alerts.length > 0 && (
              <button type="button" onClick={() => setAlerts([])}
                      style={{ ...selectStyle, cursor: 'pointer' }}>
                clear {alerts.length}
              </button>
            )}
          </form>
        </div>
      }
    >
      <div style={{ width: '100%', height: '100%', position: 'relative', minHeight: 0 }}>
        {!instrument ? (
          <div style={{
            display: 'grid', placeItems: 'center', height: '100%',
            color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
          }}>
            Select an instrument from the watchlist
          </div>
        ) : (
          <>
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

            <div ref={legendRef} style={{
              position: 'absolute', top: 6, left: 8, zIndex: 3,
              pointerEvents: 'none', fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-2xs)', color: 'var(--text-secondary)',
              whiteSpace: 'pre',
            }} />

            {/* Provenance and derivation banners. Both sit ON the chart, not in
                a tooltip: a caveat a reader has to hover to find is a caveat
                most readers never see. */}
            <div style={{
              position: 'absolute', top: 6, right: 8, zIndex: 3,
              display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end',
              maxWidth: '62%',
            }}>
              {!live && (
                <Badge tone="warn">
                  SYNTHETIC — no stored bars for {instrument} at {timeframe}.
                  {timeframe !== '1d'
                    ? ' Minute bars exist only while the market is open; try 1d.'
                    : ' Run a market-data sync to populate them.'}
                  {' '}These bars are generated, not market data.
                </Badge>
              )}
              {activeType.derived && (
                <Badge tone="info">
                  {chartType === 'heikin'
                    ? 'Heikin-Ashi: opens and closes are AVERAGED, not traded prices.'
                    : chartType === 'renko'
                    ? `Renko (brick ${fmtPrice(suggestedBrickSize(bars))}): the x-axis is not time, and a brick's close is a threshold crossed, not a print.`
                    : 'Line break: the x-axis is not time. Bars appear only when price breaks the recent range.'}
                </Badge>
              )}
              {loading && <Badge tone="muted">loading…</Badge>}
            </div>

            {showTable && (
              <div style={{
                position: 'absolute', inset: 0, zIndex: 4,
                background: 'var(--bg-panel)', overflow: 'auto',
              }}>
                <div style={{
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
                    {plotted.length} plotted bars · {live ? 'live feed' : 'SYNTHETIC'}
                    {activeType.derived && ' · derived series'}
                  </span>
                  <button onClick={() => setShowTable(false)} style={{ ...selectStyle, cursor: 'pointer' }}>
                    close
                  </button>
                </div>
                <table className="qx">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th className="r">Open</th><th className="r">High</th>
                      <th className="r">Low</th><th className="r">Close</th>
                      <th className="r">Volume</th><th className="r">Chg%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...plotted].reverse().slice(0, 300).map((b, i) => {
                      const chg = b.open ? ((b.close - b.open) / b.open) * 100 : 0
                      return (
                        <tr key={`${b.time}-${i}`}>
                          <td className="num">
                            {new Date(b.time).toLocaleString('en-GB', { hour12: false })}
                          </td>
                          <td className="num r">{fmtPrice(b.open)}</td>
                          <td className="num r">{fmtPrice(b.high)}</td>
                          <td className="num r">{fmtPrice(b.low)}</td>
                          <td className="num r">{fmtPrice(b.close)}</td>
                          <td className="num r">{Math.round(b.volume).toLocaleString('en-IN')}</td>
                          <td className="num r" style={{
                            color: chg >= 0 ? 'var(--profit)' : 'var(--loss)',
                          }}>
                            {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {last && !showTable && (
              <div style={{
                position: 'absolute', bottom: 6, left: 8, zIndex: 3,
                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                fontFamily: 'var(--font-mono)', pointerEvents: 'none',
              }}>
                {bars.length} bars · range {fmtPrice(Math.min(...bars.map((b) => b.low)))}
                {' – '}{fmtPrice(Math.max(...bars.map((b) => b.high)))}
                {' · '}
                <span style={{ color: dayChange >= 0 ? 'var(--profit)' : 'var(--loss)' }}>
                  {dayChange >= 0 ? '+' : ''}{dayChange.toFixed(2)}% over the window
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </Panel>
    </div>
  )
}

// ---------------------------------------------------------------------------

function Group({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
      {label && (
        <span style={{
          fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 2,
        }}>
          {label}
        </span>
      )}
      {children}
    </span>
  )
}

function Chip({
  active, onClick, children, title,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
  title?: string
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-pressed={active}
      style={{
        padding: '1px var(--sp-2)',
        background: active ? 'var(--accent-bg)' : 'transparent',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-2xs)',
        cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  )
}

const selectStyle: React.CSSProperties = {
  padding: '1px var(--sp-2)',
  background: 'var(--bg-input)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-secondary)',
  fontSize: 'var(--fs-2xs)',
  fontFamily: 'inherit',
}

function Badge({ tone, children }: {
  tone: 'warn' | 'info' | 'muted'
  children: React.ReactNode
}) {
  const color = tone === 'warn' ? 'var(--warn)'
              : tone === 'info' ? 'var(--info)' : 'var(--text-muted)'
  const bg = tone === 'warn' ? 'var(--warn-bg)'
           : tone === 'info' ? 'rgba(56,189,248,0.12)' : 'var(--bg-raised)'
  return (
    <span style={{
      padding: '2px 7px', background: bg, border: `1px solid ${color}`,
      borderRadius: 'var(--radius-sm)', color, fontSize: 9,
      lineHeight: 1.4, textAlign: 'right',
    }}>
      {children}
    </span>
  )
}

/** Deterministic placeholder series for instruments with no live candle
 *  source. Labelled SYNTHETIC wherever it is drawn, never as market data. */
function syntheticBars(key: string): Bar[] {
  const out: Bar[] = []
  let price = 1000
  let seed = [...key].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) & 0x7fffffff, 7)
  const rand = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff
    return seed / 0x7fffffff - 0.5
  }
  for (let i = 0; i < 240; i++) {
    const open = price
    const close = open + rand() * 12
    out.push({
      time: Date.now() - (240 - i) * 60_000,
      open, close,
      high: Math.max(open, close) + Math.abs(rand()) * 4,
      low: Math.min(open, close) - Math.abs(rand()) * 4,
      volume: 10_000 + Math.abs(rand()) * 40_000,
    })
    price = close
  }
  return out
}
